import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { postNoContent } from '@/shared/api/http';
import { clearToken } from '@/shared/auth/session';
import { SCREEN_PATHS } from '@/shared/routes';

/* Signing out.
 *
 * **There is a logout endpoint now, and the note this replaces argued there should not be.** That
 * note was right about the platform as it stood: the token was a stateless JWT with a one-hour TTL
 * and no revocation of any kind, `tenancy.yaml` said *"signing out is a client discarding it"*, and
 * a POST that answered 204 while revoking nothing would have been worse than no route — it would
 * read, to the next person maintaining this, as though the token had been invalidated server-side.
 *
 * ADR-013 changed the premise rather than the reasoning. `POST /v1/auth/logout` writes a per-subject
 * revocation epoch, so every token issued at or before that instant is refused with a 401 from the
 * next request onward. The sentence this file used to have to carry — *"a token already copied out of
 * this tab stays valid until it expires"* — is no longer true, which is the whole point of the slice.
 *
 * **What it still cannot promise, stated because the bound moved rather than vanished.** The epoch is
 * per *subject*: signing out here signs out every browser this person has, and there is no way to end
 * one session while leaving another. And a token a customer's own IdP signed is unaffected — Lumiq
 * did not mint it and cannot revoke it, so for a federated tenancy this remains a client discarding a
 * credential. `sessionStorage` rather than `localStorage` still matters for that case.
 *
 * **Four things happen, in this order, and the order is the whole design.**
 *
 * 0. `POST /v1/auth/logout` — **first, because it is the only step that needs the credential.**
 *    Clearing the token first would send the request unauthenticated and get a 401, which is the
 *    ordering bug this comment exists to prevent somebody reintroducing while tidying.
 *
 *    **Awaited, and its failure is swallowed.** A refused or unreachable request must not leave the
 *    reader signed in: the local steps below are what they can see, and a network partition on the
 *    way out would otherwise strand them on an app screen with a dead-looking button. So the server
 *    side is best-effort and the client side is not — which is the honest division, because the
 *    thing a reader is asking for is to stop being signed in *here*.
 *
 * 1. `clearToken()` — the credential goes next. Everything after this point is cleanup, so if a
 *    later step throws, the worst outcome is a tidy-up that did not finish rather than a session
 *    that is still usable. The inverse order would leave a window in which the cache had been
 *    emptied and the token had not, which is a logged-in app with no data: indistinguishable from a
 *    total outage.
 * 2. `queryClient.clear()` — every cached response. Not `invalidateQueries`, which marks entries
 *    stale and *refetches* them: that would fire a burst of requests carrying no token, each
 *    answering 401, and the 401 handler calls `clearToken()` again. `clear()` discards without
 *    fetching. This matters beyond tidiness: the identity, the dataset names and every list are
 *    keyed by query key rather than by principal, so a second person signing in on the same tab
 *    would see the first person's estate until each key happened to refetch.
 * 3. `navigate(login, { replace: true })` — replace rather than push, so the app screen the reader
 *    was on is *gone* from history rather than one Back press away.
 *
 * **`useCallback` with a stable dependency list**, so the returned function can be handed to an
 * `onClick` without re-rendering the header on every keystroke elsewhere.
 */
/* The server side of signing out. **Best-effort by design — see `useLogout`'s note.**
 *
 * **A fresh `Idempotency-Key` is passed even though the route wants none, and that follows this
 * platform's settled answer rather than arguing with it.** `postNoContent`'s own docstring makes the
 * case: the parameter is required so that omission is a *type* error rather than a runtime 428, and
 * a route the server registers in `NON_CREATING_POST_PATHS` "accepts an unnecessary key and ignores
 * it, which is what `useLogIn` already relies on". `/login` is in that registry for the same reason
 * `/logout` now is, and sends a key. Making the parameter optional for this one caller would weaken
 * it for the twenty that must have it.
 *
 * `postNoContent` rather than `post`: the route answers 204, and `response.json()` on an empty body
 * throws — so routing a 204 through `post` would report a successful sign-out as a malformed
 * response. That is the distinction that helper exists for.
 *
 * An empty body rather than none, because the helper always sets `Content-Type` and serialises: `{}`
 * is what the server ignores, where `undefined` would send the literal string "undefined".
 *
 * **Every outcome is swallowed.** A 401 means the session had already gone, which is the outcome
 * being asked for; anything else is a network or server fault the reader cannot act on, and the local
 * steps that follow are what they can actually see.
 */
async function signOutOnServer(): Promise<void> {
  try {
    await postNoContent('/v1/auth/logout', crypto.randomUUID(), {});
  } catch {
    /* Deliberately empty. The reader is being signed out locally either way, and a banner about a
     * failed sign-out on a screen they are about to leave is noise they cannot act on. */
  }
}

export function useLogout(): () => void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(() => {
    /* Fired before the token is cleared, because it is the one step that needs it — and not awaited
     * by the caller, so the redirect below is never held up by a slow network. The promise is
     * consumed here rather than left floating: an unhandled rejection in a click handler is a
     * console error a reader cannot act on, and there is nothing useful to do with the failure. */
    void signOutOnServer();

    /* No try/catch around `clearToken`: it is already total — every storage access inside it is
     * individually guarded, because a browser with storage disabled throws on access rather than
     * returning null, and it clears the in-memory copy first. So there is no failure mode here that
     * a handler could improve on, and wrapping it would suggest one exists. */
    clearToken();

    try {
      queryClient.clear();
    } catch {
      /* **Caught deliberately, and this is the one step that can fail without mattering.** The
       * credential is already gone and the redirect below is already going to happen, so a cache
       * that refused to empty costs a re-fetch rather than a leak. Letting it propagate would
       * abandon the navigation and leave the reader on a signed-out app screen — the inconsistent
       * state this ordering exists to prevent. */
    }

    navigate(SCREEN_PATHS.login, { replace: true });
  }, [navigate, queryClient]);
}
